<template>
  <div class="error-container text-white">
    <div class="logo-container mx-auto" style="width: 10em">
      <div class="img-container mb-1">
        <div v-dompurify-html:svg="identity?.logoUrl" class="logo"></div>
      </div>
    </div>
    <template v-if="status == 404">
      <div>
        <h1 class="text-center">Vous semblez vous être égaré&#183;e sur la</h1>

        <div class="route-404">
          <div class="img-container">
            <img :src="logoUrl" alt="404" />
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="text-center">
        <h1>Désolé</h1>
        <h1>Erreur {{ status }}</h1>
        <div>{{ message }}</div>
      </div>
    </template>

    <div class="text-container text-center mb-3">
      <p class="m-0 mt-4">Nous nous excusons de ce détour et vous invitons à retourner à</p>
      <p class="m-0">
        <span class="link-to-home" @click="goHome()">
          l'accueil du service de concierge de
          {{ contact?.organization }}
        </span>
      </p>
    </div>
    <div class="contact-container">
      <div class="list-group no-gutters">
        <span :href="contact?.website?.url" class="list-group-item">
          En cas de nécessité vous pouvez
        </span>
        <a :href="contact?.website?.url" class="list-group-item list-group-item-action web">
          <em></em>
          Visiter notre <span>site web</span>
        </a>
        <a :href="'tel:' + contact?.phone" class="list-group-item list-group-item-action phone">
          <em></em>
          Nous appeler au <span>{{ contact?.formattedPhone }}</span>
        </a>
        <a :href="'mailto:' + contact?.email" class="list-group-item list-group-item-action email">
          <em></em>
          Nous écrire au <span>{{ contact?.email }}</span>
        </a>
      </div>
    </div>
    <div v-if="identity" class="footer container text-white">
      <span class="footer-links footer-links--main-website d-block d-md-none">
        <a
          :href="identity?.contact?.website?.url"
          class="text-white font-weight-light"
          target="_blank"
        >
          {{ identity?.contact?.website?.label }}
        </a>
      </span>
      <div
        class="footer-powered-by d-flex flex-column justify-content-center align-items-center mb-3"
        style="gap: 1em"
      >
        <div>
          <span>Propulsé par <strong>Alfred</strong><small>&trade;</small></span
          >,
          <span>le PRO des RDV en ligne</span>
        </div>
        <div
          class="d-flex flex-column justify-content-center align-items-center"
          style="gap: 0.5em"
        >
          <span>pour {{ identity?.contact?.organization }}</span>
          <span>{{ identity?.contact?.address }}</span>
        </div>
      </div>
      <div class="footer-logo">
        &copy; {{ currentYear }}
        <InfernalMediaLogo variant="light" class="ms-3 logo" @click="openInfernalMedia()" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import { onUnmounted, onMounted, nextTick } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { useAppointmentStore } from '~conciergeUiCommons/store/appointmentStore'
import { useSubmissionFormStore } from '~conciergeUiCommons/store/submissionFormStore'
import InfernalMediaLogo from '~conciergeUiCommons/components/InfernalMediaLogo.vue'
import logoUrl from '~concierge-images/route404.svg'

const currentYear = new Date().getFullYear()
const identityStore = useIdentityStore()
const appointmentStore = useAppointmentStore()
const submissionFormStore = useSubmissionFormStore()
const resetStore = () => {
  appointmentStore.$reset()
  submissionFormStore.$reset()
}
const page = usePage()

const identity = page.props.identity ?? identityStore.identity
const contact = page.props.identity?.contact ?? identityStore.identity?.contact
export interface Props {
  status: number
  message: string
  identity: any
}

function openInfernalMedia() {
  const url = 'https://infernal.media'
  window.open(url, '_blank')
}
async function goHome(): Promise<void> {
  resetStore()
  await nextTick()
  router.visit(page.props.homeUrl)
}

onMounted(() => document.querySelector('body')?.classList.add('error-page'))
onUnmounted(() => document.querySelector('body')?.classList.remove('error-page'))

withDefaults(defineProps<Props>(), {
  status: 400,
  message: 'Mauvaise Demande',
  identity: null
})
</script>
<style lang="scss">
@use '~concierge/styles/bootstrap' as *;

body.error-page {
  --bg-img: url('~concierge-images/error.jpg') !important;
  background-image: var(--bg-img) !important;
  background-blend-mode: unset;
  background-size: cover;
  background-position: center center;
  background-attachment: fixed;

  .route-404 {
    display: block;
    width: 150px;
    margin: 1em auto;
    padding: 1em;
  }

  #app {
    min-height: 100vh;
    max-width: 100vw;
    display: block;
  }

  .error-container {
    height: 100dvh;
    margin: 0 auto;
    display: grid;
    grid-template-rows: 11% 215px 11% 11% auto;

    .text-container {
      .link-to-home {
        border-bottom: 1px solid;
        border-color: white;
        padding-bottom: 0.02em;
        transition: border-bottom-color 0.4s ease-in-out;

        &:hover {
          cursor: pointer;
          border-bottom-color: transparent;
        }
      }
    }

    .contact-container {
      align-self: top;
      width: 100%;
      text-align: center;

      .list-group-item-action {
        width: 100%;
        padding-bottom: 0.3em;
        padding-top: 1em;
        font-weight: 400;

        span {
          border-bottom: 1px solid;
          border-color: white;
          padding-bottom: 0.02em;
          transition: border-bottom-color 0.4s ease-in-out;

          &:hover {
            border-bottom-color: transparent;
          }
        }
      }

      .list-group {
        --bs-list-group-border-color: rgb(255 255 255 / 4%);

        .list-group-item {
          color: white;
          background-color: transparent;
          border-top: none;
          border-left: none;
          border-right: none;

          &:last-child {
            border-radius: 0;
          }
        }
      }
    }
  }

  .logo {
    display: block;
  }

  .logo svg,
  .logo svg * {
    fill: white !important;
  }

  .footer {
    align-self: center;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background-color: transparent;
    color: white;
    font-size: 1.1rem;
    flex-direction: column;

    .footer-logo {
      display: flex;
      place-items: center;
    }

    .footer-logo svg {
      max-width: 70px;
      height: auto;
      cursor: pointer;
    }

    .footer-links {
      display: inline-flex;
      place-items: center;

      span,
      a {
        font-weight: 400;
        color: white;
        text-decoration: none;
        cursor: pointer;

        &:hover {
          color: white;
          text-decoration: underline;
        }
      }
    }

    .footer-powered-by {
      font-size: clamp(0.8rem, 1rem, 1.2rem);
      padding: 0.3em;

      em {
        font-style: normal;
      }
    }
  }
}
</style>
